import axios from 'axios'
import React, { useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useForm } from 'react-hook-form'
import Gtm from '../../utils/gtm'
import { loggingEnabled } from '../../utils/logging'

const Esub = ({
  content,
  img,
  imgMobile,
  title,
  brand,
  culture,
  seg,
  sourceCode,
  sourceSystem,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm() // initialize the hook

  const [formSubmitting, setFormSubmitting] = useState(false)
  const [formStatus, setFormStatus] = useState(null)
  const gtm = new Gtm(loggingEnabled)

  if (!title) return null

  const _submitEmailForm = data => {
    const params = new URLSearchParams()
    params.append('email', data.email)
    params.append('firstName', data.firstName)

    params.append('brand', brand)
    params.append('culture', culture)
    params.append('seg', seg)
    params.append('sourceCode', sourceCode)
    params.append('sourceSystem', sourceSystem)

    setFormSubmitting(true)
    axios
      .post('/esub', params)
      .then(({ data }) => {
        setFormSubmitting(false)
        setFormStatus(data.status)
        gtm.emailSubscription(seg)
      })
      .catch(err => {
        console.error(err)
      })
  }

  const useStyles = createUseStyles(theme => ({
    container: {
      padding: '140px 15px',
      backgroundColor: '#000',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
      backgroundSize: 'cover',
      backgroundImage: `url("${img}")`,
      '@media (max-width: 600px)': {
        backgroundImage: `url("${imgMobile}")`,
        padding: '90px 15px',
      },
    },
    innerCont: { margin: '0 auto', width: '100%', maxWidth: '1220px' },
    title: {
      fontSize: '32px',
      lineHeight: '42px',
      fontWeight: 700,
      fontFamily: 'TTRounds',
      textAlign: 'left',
      marginBottom: '20px',
      marginTop: 0,
      color: '#fff',
      '@media (max-width: 600px)': {
        fontSize: '28px',
        lineHeight: '34px',
      },
    },
    details: {
      fontSize: '16px',
      lineHeight: '19px',
      fontWeight: 400,
      fontFamily: 'AvenirNextLTPro',
      textAlign: 'left',
      marginBottom: '20px',
      marginTop: 0,
      maxWidth: '560px',
      color: '#fff',
    },
    input: {
      boxSizing: 'border-box',
      borderRadius: '17px',
      padding: '12px 15px 10px',
      fontSize: '16px',
      lineHeight: '19px',
      fontWeight: 300,
      fontFamily: 'AvenirNextLTPro',
      width: '100%',
      border: 'none',
      boxShadow: '1px 5px 15px rgba(0, 0, 0, 0.15)',
    },
    inputs: {
      display: 'flex',
      flexDirection: 'rows',
      '@media (max-width: 600px)': {
        flexDirection: 'column',
      },
    },
    inputCont: {
      boxSizing: 'border-box',
      width: '280px',
      marginRight: '10px',
      '@media (max-width: 600px)': {
        display: 'block',
        width: '100%',
        marginRight: '0px',
        marginBottom: '15px',
      },
    },
    buttonCont: {
      marginTop: '15px',
      '@media (max-width: 600px)': {
        marginTop: '0px',
      },
    },
    button: {
      borderRadius: '15px',
      padding: '12px 22px 10px',
      fontSize: '14px',
      lineHeight: '19px',
      fontWeight: 300,
      fontFamily: 'TTRounds',
      color: '#fff',
      border: 'none',
      backgroundColor: theme.colors.orange,
      '&:hover': {
        backgroundColor: theme.colors.orangeHover,
        cursor: 'pointer',
      },
    },
    returnMessage: {
      borderRadius: '5px',
      backgroundColor: '#fff',
      color: '#2d2d2d',
      padding: '12px',
      marginTop: '25px',
      fontSize: '16px',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      maxWidth: '560px',
      padding: '30px 20px',
      boxSizing: 'border-box',
      boxShadow: '1px 5px 15px rgba(0, 0, 0, 0.15)',
      borderRadius: '24px',
      '& .erTitle': {
        fontFamily: 'TTRounds',
        fontSize: '26px',
        lineHeight: '30px',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 5,
        fontWeight: 700,
      },
      '& .erBody': {
        fontSize: '18px',
        lineHeight: 1.5,
        textAlign: 'center',
      },
    },
    error: {
      color: '#EE2049',
      fontSize: 12,
      paddingTop: 7,
    },
  }))

  const classes = useStyles()

  return (
    <div className={classes.container}>
      <div className={classes.innerCont}>
        <div className={classes.title}>{title}</div>
        <div className={classes.details} dangerouslySetInnerHTML={{ __html: content }} />
        <form method="POST" onSubmit={handleSubmit(_submitEmailForm)}>
          <div className={classes.inputs}>
            <div className={classes.inputCont}>
              <input
                type="text"
                {...register('firstName', { required: true })}
                placeholder="First Name"
                className={classes.input}
              />
              {errors.firstName && <div className={classes.error}>First name is required.</div>}
            </div>
            <div className={classes.inputCont}>
              <input
                type="email"
                {...register('email', { required: true, pattern: /^\S+@\S+$/i })}
                placeholder="Email Address"
                className={classes.input}
              />
              {errors.email && (
                <div className={classes.error}>A valid email address is required.</div>
              )}
            </div>
          </div>

          {formStatus !== true && (
            <div className={classes.buttonCont}>
              <button type="submit" className={classes.button} disabled={formSubmitting}>
                Sign Up
              </button>
            </div>
          )}

          <div>
            {formStatus === false && (
              <div className={classes.returnMessage}>
                <div className="erTitle">
                  <img
                    src="/themes/thrifty/assets/images/error.png"
                    width={20}
                    style={{ marginRight: '7px', marginTop: '-2px' }}
                  />{' '}
                  Ohh no.
                </div>
                <div className="erBody">
                  There was a problem submitting the form, please try again later
                </div>
              </div>
            )}
            {formStatus === true && (
              <div className={classes.returnMessage}>
                <div className="erTitle">
                  <img
                    src="/themes/thrifty/assets/images/success.png"
                    width={20}
                    style={{ marginRight: '7px', marginTop: '-2px' }}
                  />{' '}
                  Thank you
                </div>
                <div className="erBody">for signing up to our mailing list!</div>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

export default Esub
